/**
 * Role API Service
 *
 * Handles all role and permission management API calls.
 * Uses NODEID from MFUN_TBL mapped to MURP_TBL for permissions.
 *
 * Data Flow:
 * 1. Menu functions loaded during login via getPermissionsByRole()
 * 2. Build tree structure using buildFunctionTreeFromMenuList utility
 * 3. Get roles from master codes (shared with other components via React Query cache)
 * 4. Get raw permissions from /api/v1/roles/permissions
 * 5. Combine data for permission matrix display
 */
import { apiClient } from '@/lib/axiosClient';
import { API_CONFIG } from '@/constants';
import { logger } from '@/shared/utils/logger';
import type { ApiResponse } from './types';

const ROLE_PREFIX = `${API_CONFIG.API_PREFIX}/roles`;

// ============================================================================
// Types
// ============================================================================

/**
 * Function node for permission matrix tree (nodeId-based for MURP_TBL)
 */
export interface FunctionTreeNode {
  nodeId: number;
  functionId: string | null;
  functionName: string | null;
  parentId: number | null;
  sequenceNo: number;
  children?: FunctionTreeNode[];
}

/**
 * Role info for permission matrix columns
 */
export interface RoleInfo {
  code: string;
  description: string;
}

/**
 * Raw permission item from MURP_TBL
 */
export interface RawPermissionItem {
  roleCode: string;
  nodeId: number;
  programId: string | null;
  userId: string | null;
  lastUpdate: string | null;
}

/**
 * Permission matrix combined data for UI
 */
export interface PermissionMatrix {
  /** Tree structure of menu functions (rows) */
  functions: FunctionTreeNode[];
  /** List of roles (columns) */
  roles: RoleInfo[];
  /** nodeId -> roleCode -> hasPermission (for checkbox state) */
  permissions: Record<number, Record<string, boolean>>;
}

/**
 * Permission update item for save API
 */
export interface PermissionUpdate {
  roleCode: string;
  nodeId: number;
  hasPermission: boolean;
}

/**
 * Raw permissions API response
 */
interface RawPermissionsResponse {
  status: string;
  message: string;
  timestamp: string;
  permissions: RawPermissionItem[];
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Create a FunctionTreeNode from a raw MenuFunctionItem.
 */
function createFunctionNode(menu: MenuFunctionItem): FunctionTreeNode {
  return {
    nodeId: menu.nodeId,
    functionId: menu.functionId,
    functionName: menu.functionName,
    parentId: menu.parentId,
    sequenceNo: menu.sequenceNo ?? 0,
  };
}

/**
 * Place a node into its parent's children list, or into rootItems if no parent found.
 */
function assignNodeToHierarchy(
  node: FunctionTreeNode,
  menu: MenuFunctionItem,
  nodeMap: Map<number, FunctionTreeNode>,
  rootItems: FunctionTreeNode[],
): void {
  if (menu.parentId === null) {
    rootItems.push(node);
    return;
  }
  const parent = nodeMap.get(menu.parentId);
  if (!parent) {
    // Parent not found - add to root
    rootItems.push(node);
    return;
  }
  if (!parent.children) {
    parent.children = [node];
  } else {
    parent.children.push(node);
  }
}

/**
 * Build function tree from flat menu data (MenuFunctionItem from permissionStore)
 * Used by useRoles hook to convert menuList to tree structure
 */
function buildFunctionTree(
  menuFunctions: MenuFunctionItem[],
): FunctionTreeNode[] {
  if (!menuFunctions || menuFunctions.length === 0) {
    return [];
  }

  const nodeMap = new Map<number, FunctionTreeNode>();
  const rootItems: FunctionTreeNode[] = [];

  // First pass: create all nodes
  for (const menu of menuFunctions) {
    if (typeof menu.nodeId !== 'number') {
      continue;
    }
    nodeMap.set(menu.nodeId, createFunctionNode(menu));
  }

  // Second pass: build hierarchy
  for (const menu of menuFunctions) {
    const node = nodeMap.get(menu.nodeId);
    if (!node) {
      continue;
    }
    assignNodeToHierarchy(node, menu, nodeMap, rootItems);
  }

  return sortAndCleanTree(rootItems);
}

/**
 * Sort tree items by sequenceNo and remove empty children arrays
 */
function sortAndCleanTree(items: FunctionTreeNode[]): FunctionTreeNode[] {
  const sorted = [...items].sort((a, b) => a.sequenceNo - b.sequenceNo);
  return sorted.map(item => {
    if (item.children && item.children.length > 0) {
      item.children = sortAndCleanTree(item.children);
    } else {
      delete item.children;
    }
    return item;
  });
}

/**
 * Convert raw permissions array to lookup map
 * Map: nodeId -> roleCode -> true
 */
function buildPermissionMap(
  permissions: RawPermissionItem[],
): Record<number, Record<string, boolean>> {
  const map: Record<number, Record<string, boolean>> = {};

  for (const perm of permissions) {
    if (!map[perm.nodeId]) {
      map[perm.nodeId] = {};
    }
    map[perm.nodeId][perm.roleCode] = true;
  }

  return map;
}

// ============================================================================
// API Service
// ============================================================================

export const roleApi = {
  /**
   * Build function tree from menuList stored in permissionStore
   * Used by useRoles hook to convert flat menu to tree for permission matrix
   *
   * @param menuList - Flat menu array from permissionStore.permissions.menuList
   */
  buildFunctionTreeFromMenuList: (
    menuList: MenuFunctionItem[],
  ): FunctionTreeNode[] => {
    return buildFunctionTree(menuList);
  },

  /**
   * Get raw permissions from MURP_TBL
   */
  getRawPermissions: async (): Promise<RawPermissionItem[]> => {
    const response = await apiClient.get<RawPermissionsResponse>(
      `${ROLE_PREFIX}/permissions`,
    );
    return response.data?.permissions ?? [];
  },

  /**
   * Build permission matrix from raw data
   * Called by usePermissionMatrix hook after fetching data separately
   *
   * @param functions - Function tree (from buildFunctionTreeFromMenuList)
   * @param roles - Role list (from master codes, cached via useMasterCodes)
   * @param rawPermissions - Raw permissions (from getRawPermissions)
   */
  buildPermissionMatrix: (
    functions: FunctionTreeNode[],
    roles: RoleInfo[],
    rawPermissions: RawPermissionItem[],
  ): PermissionMatrix => {
    const permissions = buildPermissionMap(rawPermissions);

    logger.debug('Permission matrix built', {
      functionsCount: functions.length,
      rolesCount: roles.length,
      permissionsCount: rawPermissions.length,
    });

    return {
      functions,
      roles,
      permissions,
    };
  },

  /**
   * Save permission matrix (bulk update)
   *
   * Only sends changed permissions to backend.
   * Backend will:
   * - INSERT for hasPermission=true (new)
   * - DELETE for hasPermission=false (removed)
   *
   * @param permissions - Array of changed permissions only
   */
  savePermissionMatrix: async (
    permissions: PermissionUpdate[],
  ): Promise<ApiResponse<{ affectedRoles: string[] }>> => {
    const response = await apiClient.post<
      ApiResponse<{ affectedRoles: string[] }>
    >(`${ROLE_PREFIX}/permissions`, { permissions });
    return response.data;
  },

  /**
   * Get permissions for a specific role
   * Used on login to load user's permissions and menu list
   *
   * @param roleCode - The role code (e.g., AD, U, M)
   * @returns Permissions with nodeIds, functionIds, menuList, plus isAdmin flag
   */
  getPermissionsByRole: async (
    roleCode: string,
  ): Promise<UserRolePermissions> => {
    const response = await apiClient.get<UserRolePermissionsResponse>(
      `${ROLE_PREFIX}/permissions/${roleCode}`,
    );
    return {
      roleCode: response.data.roleCode,
      permissions: response.data.permissions,
      allowedFunctions: response.data.allowedFunctions,
      allowedNodeIds: response.data.allowedNodeIds ?? [],
      menuList: response.data.menuList ?? [],
      isAdmin: response.data.isAdmin ?? false,
    };
  },
};

// ============================================================================
// Types for User Permissions (login flow)
// ============================================================================

/**
 * Permission item with functionId for route matching
 */
export interface UserPermissionItem {
  nodeId: number;
  functionId: string | null;
}

/**
 * Menu function item from MFUN_TBL
 */
export interface MenuFunctionItem {
  nodeId: number;
  functionId: string | null;
  functionName: string | null;
  parentId: number | null;
  sequenceNo: number;
  programId: string;
  userId: string;
  path: string | null;
  lastUpdate: string;
}

/**
 * User's permissions from API
 * Includes both permissions and full menu list for sidebar/role screens
 */
export interface UserRolePermissions {
  roleCode: string;
  permissions: UserPermissionItem[];
  allowedFunctions: string[];
  allowedNodeIds: number[];
  /** Full menu list for sidebar and role management */
  menuList: MenuFunctionItem[];
  /** True if user has Admin role (ADMIN) - has full access to all functions */
  isAdmin: boolean;
}

/**
 * API response for user permissions
 */
interface UserRolePermissionsResponse {
  status: string;
  message: string;
  timestamp: string;
  roleCode: string;
  permissions: UserPermissionItem[];
  allowedFunctions: string[];
  allowedNodeIds: number[];
  menuList: MenuFunctionItem[];
  /** True if user has Admin role (ADMIN) - has full access to all functions */
  isAdmin: boolean;
}
